/**
 * Admin Insights overview (P3-S5 Doc 46) — list all users summaries + manual trigger.
 */
import {
  CalendarRange,
  Loader2,
  RefreshCw,
  Sparkles,
  Zap,
} from "lucide-react";
import * as React from "react";

import { adminInsightsApi, type SummaryOut } from "@/api/insights";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useUIStore } from "@/stores/ui";

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function AdminInsightsPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<SummaryOut[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [triggeringId, setTriggeringId] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminInsightsApi.list(50);
      setItems(r.items);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tải được",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleTrigger = async (
    userId: string,
    userEmail: string | null | undefined,
  ): Promise<void> => {
    setTriggeringId(userId);
    try {
      const r = await adminInsightsApi.trigger(userId, "previous_week");
      if (r.ok) {
        pushToast({
          variant: "success",
          title: "Đã tạo summary",
          description: userEmail ?? userId,
        });
        await refresh();
      } else {
        pushToast({
          variant: "destructive",
          title: "Không tạo được",
          description: r.error ?? "Lỗi không rõ",
        });
      }
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tạo được",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setTriggeringId(null);
    }
  };

  return (
    <div className="container max-w-6xl py-6 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="size-6 text-primary" /> AI Insights — Admin
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Xem all-user weekly summaries + trigger manual gen. Beat tự gen
            mỗi Chủ Nhật 23:50 UTC. Doc 46.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refresh()}>
          <RefreshCw className="size-4 mr-1.5" /> Reload
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-6 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Đang tải…
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center space-y-2">
            <Sparkles className="size-8 mx-auto text-muted-foreground" />
            <p className="font-medium">Chưa có summary nào</p>
            <p className="text-sm text-muted-foreground">
              Cuối tuần đầu tiên beat task tự chạy. Hoặc bấm trigger thủ công
              từ tab Users.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b bg-muted/40">
                <tr>
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2">Tuần</th>
                  <th className="px-4 py-2">Stats</th>
                  <th className="px-4 py-2">Provider</th>
                  <th className="px-4 py-2">Summary (preview)</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b last:border-0">
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium">
                        {it.user_email ?? it.user_id}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        {it.user_id.slice(0, 8)}…
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-0.5 rounded">
                        <CalendarRange className="size-3" />
                        {fmtDate(it.period_start)}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                      {it.message_count} msg
                      <br />
                      {it.conversation_count} convs
                    </td>
                    <td className="px-4 py-3 align-top text-xs">
                      {it.llm_provider ? (
                        <span className="font-mono">
                          {it.llm_provider}
                          {it.llm_model ? `:${it.llm_model}` : ""}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                      {it.tokens_used ? (
                        <div className="text-[10px] text-muted-foreground">
                          {it.tokens_used} tokens
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <p className="text-xs line-clamp-3">
                        {it.summary_text}
                      </p>
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={triggeringId === it.user_id}
                        onClick={() =>
                          void handleTrigger(it.user_id, it.user_email)
                        }
                        title="Trigger gen lại tuần trước"
                      >
                        {triggeringId === it.user_id ? (
                          <Loader2 className="size-3 animate-spin mr-1" />
                        ) : (
                          <Zap className="size-3 mr-1" />
                        )}
                        Re-gen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
